import React, { FC, useState, useEffect, useMemo } from "react";
import { useIntl } from "react-intl";
import { Props, defaultData } from "./types";
import { fetchDailyVerse, getManualComparison, DailyVerse } from "../../../dashboard/dailyVerse";
import { db } from "../../../db/state";
import { useValue } from "../../../lib/db/react";
import { messages } from "./messages";
import { getAvailableVersions, getDefaultVersionId } from "../../../services/bible";

const getLocaleOverride = (): string | null => {
  try { return new URLSearchParams(window.location.search).get("locale"); } catch { return null; }
};

const Verse: FC<Props> = ({ data = defaultData }) => {
  const dbLocale = useValue(db, "locale");
  const locale = getLocaleOverride() || dbLocale;
  const intl = useIntl();
  const [daily, setDaily] = useState<DailyVerse | DailyVerse[] | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const mode = data.mode || defaultData.mode;
  const seedOffset = data.seedOffset ?? 0;
  const verseFontSize = data.verseFontSize ?? defaultData.verseFontSize;
  const verseFontWeight = data.verseFontWeight ?? defaultData.verseFontWeight;
  const referenceFontSize = data.referenceFontSize ?? defaultData.referenceFontSize;
  const referenceFontWeight = data.referenceFontWeight ?? defaultData.referenceFontWeight;
  const sortedManualVersionIds = useMemo(() => {
    const all = getAvailableVersions();
    const defaultId = getDefaultVersionId(locale);
    const defaultVersion = all.find((v) => v.id === defaultId);
    if (!defaultVersion) return data.manualVersionIds || [];
    const ordered = [...all.filter((v) => v.language === defaultVersion.language), ...all.filter((v) => v.language !== defaultVersion.language)];
    const selected = new Set(data.manualVersionIds || []);
    return ordered.map((v) => v.id).filter((id) => selected.has(id));
  }, [locale, JSON.stringify(data.manualVersionIds)]);


  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);

    if (mode === 'manual') {
      const book = data.manualBook;
      const chapter = data.manualChapter;
      const verse = data.manualVerse;
      const versionIds = sortedManualVersionIds;
      if (book && chapter && verse && versionIds && versionIds.length > 0) {
        getManualComparison(locale, book, chapter, verse, versionIds)
          .then((verses) => {
            if (!cancelled) {
              setDaily(verses);
              setLoading(false);
            }
          })
          .catch((err) => {
            console.error("DailyVerse manual fetch failed:", err);
            if (!cancelled) {
              const msg = (err instanceof Error && err.message) ? err.message : intl.formatMessage(messages.error);
              setError(msg);
              setLoading(false);
            }
          });
      } else {
        setLoading(false);
      }
    } else {
      fetchDailyVerse(locale, mode, seedOffset)
        .then((verse) => {
          if (!cancelled) {
            setDaily(verse);
            setLoading(false);
          }
        })
        .catch((err) => {
          console.error("DailyVerse fetch failed:", err);
          if (!cancelled) {
            const msg = (err instanceof Error && err.message) ? err.message : intl.formatMessage(messages.error);
            setError(msg);
            setLoading(false);
          }
        });
    }
    return () => {
      cancelled = true;
    };
  }, [locale, mode, seedOffset, data.manualBook, data.manualChapter, data.manualVerse, JSON.stringify(sortedManualVersionIds)]);

  const verses = Array.isArray(daily) ? daily : (daily ? [daily] : []);
  const displayText = verses.map((v) => v.text).join("\n\n") || "";
  const sharedReference = verses[0]?.reference || "";

  if (loading && !displayText) {
    return (
      <div className="Verse verse-cinematic" style={{ color: "inherit", paddingTop: 60 }}>
        <div className="verse-cinematic__text" style={{ opacity: 0.5 }}>
          {intl.formatMessage(messages.loading)}
        </div>
      </div>
    );
  }

  if (error && !displayText) {
    return (
      <div className="Verse verse-cinematic" style={{ color: "inherit", paddingTop: 60 }}>
        <div className="verse-cinematic__text" style={{ fontStyle: "italic" }}>
          {intl.formatMessage(messages.error)}
        </div>
      </div>
    );
  }

  if (!displayText) {
    return (
      <div className="Verse verse-cinematic" style={{ color: "inherit", paddingTop: 60 }}>
        <div className="verse-cinematic__text" style={{ fontStyle: "italic" }}>
          {intl.formatMessage(messages.placeholder)}
        </div>
      </div>
    );
  }

  return (
    <div className="Verse verse-cinematic" style={{ color: "inherit" }}>
      {loading && (
        <div style={{ marginBottom: 8, fontSize: 12, opacity: 0.5, fontStyle: "italic" }}>
          {intl.formatMessage(messages.loading)}
        </div>
      )}
      <div style={{ opacity: loading ? 0.4 : 1, transition: "opacity 0.2s" }}>
        {verses.map((v, i) => (
          <div key={i} className="verse-cinematic__line" style={{ display: verses.length > 1 ? "block" : undefined, marginBottom: verses.length > 1 ? 8 : 0 }}>
            <blockquote
              className="verse-cinematic__text"
              style={{
                fontSize: `${verseFontSize}px`,
                fontWeight: verseFontWeight,
                lineHeight: 1.6,
                color: "inherit",
              }}
            >
              {v.text}
            </blockquote>
            {verses.length === 1 && v.reference && (
              <cite
                className="verse-cinematic__ref"
                style={{
                  fontSize: `${referenceFontSize}px`,
                  fontWeight: referenceFontWeight,
                  fontStyle: "normal",
                  color: "inherit",
                }}
              >
                — {v.reference}
              </cite>
            )}
          </div>
        ))}
        {verses.length > 1 && sharedReference && (
          <cite
            className="verse-cinematic__ref"
            style={{
              fontSize: `${referenceFontSize}px`,
              fontWeight: referenceFontWeight,
              fontStyle: "normal",
              color: "inherit",
            }}
          >
            — {sharedReference}
          </cite>
        )}
      </div>
    </div>
  );
};

export default Verse;
